// The saved-deck fields a content save derives from an analysis, as ONE pure
// function shared by useSavedDecks.saveDeck and the deck-box runner's commit
// (plan 1b: "Runner and saveDeck must agree").
//
//   savedDeckFields      - commander / engine / bracket / card count, exactly
//                          as saveDeck has always derived them.
//   deckBoxCommanders    - the runner's commander + partner, from the sync
//                          manifest's `// Commander` section cross-checked
//                          against `manifest.zone` (plan 11 R2-5, 12 R3-3).
//   deckBoxCommitFields  - the commit body's derived fields.
import { type CompleteAnalysis, displayBracket } from '../api/types'
import { canonicalizeCardName } from './canonicalCardName'

/** The analysis' own commander (commander_analysis.name), or null. */
export function extractCommander(analysis: CompleteAnalysis): string | null {
  const ca = analysis.commander_analysis
  if (ca && typeof ca === 'object' && 'name' in ca) {
    return ca.name as string
  }
  return null
}

export interface SavedDeckFields {
  commander: string | null
  engine: string
  bracket: number
  cardCount: number
}

/**
 * The fields a full (non-instant) saveDeck writes from the analysis. The
 * defaults are saveDeck's: engine 'unknown', bracket 3 without a bracket
 * analysis, 0 cards without deck_stats.
 */
export function savedDeckFields(analysis: CompleteAnalysis): SavedDeckFields {
  return {
    commander: extractCommander(analysis),
    // eslint-disable-next-line i18next/no-literal-string -- stored identity code (engine)
    engine: analysis.ipom_analysis?.engine?.primary_engine ?? 'unknown',
    bracket: analysis.bracket_analysis ? displayBracket(analysis.bracket_analysis) : 3,
    cardCount: analysis.deck_stats?.total_cards ?? 0,
  }
}

// --- Deck-box runner: commander + partner -------------------------------------

/** One line of a manifest's `// Commander` section. */
export interface CommanderLine {
  /** The decklist line without its quantity prefix: what gets STORED. */
  displayName: string
  /** canonicalizeCardName(displayName): what gets COMPARED. */
  key: string
}

const COMMANDER_HEADER = /^\/\/\s*commanders?\s*$/i
const QTY_PREFIX = /^\d+\s*[xX]?\s+/

/**
 * The `// Commander` section of a sync manifest's decklist, in decklist order,
 * one entry per distinct key (a card listed twice, once by its full DFC name,
 * is one entry: the first). The section ends at a blank line or the next
 * `//` header.
 */
export function parseCommanderSection(decklist: string): CommanderLine[] {
  const out: CommanderLine[] = []
  const seen = new Set<string>()
  let inSection = false
  for (const raw of decklist.split(/\r?\n/)) {
    const line = raw.trim()
    if (!inSection) {
      if (COMMANDER_HEADER.test(line)) inSection = true
      continue
    }
    if (!line || line.startsWith('//')) break
    const displayName = line.replace(QTY_PREFIX, '').trim()
    const key = canonicalizeCardName(displayName)
    if (!displayName || !key || seen.has(key)) continue
    seen.add(key)
    out.push({ displayName, key })
  }
  return out
}

export interface DeckBoxCommanders {
  commander: string | null
  partner: string | null
  /**
   * True when the analysis' primary could not be placed cleanly (it matched
   * zero or several zone entries, the section disagrees with manifest.zone,
   * there is neither a section nor an analysis commander, or more than one
   * entry remained for the partner slot). The caller logs
   * it; the fields are still the documented fallback.
   */
  mismatch: boolean
}

/**
 * Commander + partner for a deck-box commit (plan 11 R2-5 as corrected by
 * 12 R3-3). Display names come from the decklist, never keys:
 *
 *   1. Parse the `// Commander` section into {displayName, key}.
 *   2. Cross-check: its key set must equal the manifest's `zone` exactly
 *      (zone is canonical front keys, plan 13.7).
 *   3. Primary = the analysis commander, matched by key to exactly ONE
 *      section entry. Partner = the single entry left after removing it (none
 *      left: null; several left: null and mismatch).
 *   4. Otherwise (no analysis commander, zero or several matches, failed
 *      cross-check): commander = the first entry in decklist order, partner =
 *      the second when there are exactly two, else null; mismatch.
 */
export function deckBoxCommanders(
  analysis: CompleteAnalysis,
  manifest: { decklist: string; zone: readonly string[] },
): DeckBoxCommanders {
  const entries = parseCommanderSection(manifest.decklist)
  const sectionKeys = new Set(entries.map(e => e.key))
  const zoneKeys = new Set(manifest.zone)
  const agrees = sectionKeys.size === zoneKeys.size && [...sectionKeys].every(k => zoneKeys.has(k))

  const primaryName = extractCommander(analysis)
  const primaryKey = primaryName ? canonicalizeCardName(primaryName) : ''
  const matches = primaryKey ? entries.filter(e => e.key === primaryKey) : []

  if (agrees && matches.length === 1) {
    const rest = entries.filter(e => e.key !== primaryKey)
    return {
      commander: matches[0].displayName,
      partner: rest.length === 1 ? rest[0].displayName : null,
      mismatch: rest.length > 1,
    }
  }
  return {
    commander: entries[0]?.displayName ?? null,
    partner: entries.length === 2 ? entries[1].displayName : null,
    // Always logged: even with no zone section AND no analysis commander the
    // runner could not place a commander, which is worth seeing.
    mismatch: true,
  }
}

/** The derived fields of a deck-box commit body (the API's names). */
export interface DeckBoxCommitFields {
  bracket: number
  engine: string
  card_count: number
  commander: string | null
  partner: string | null
}

/**
 * The commit's derived fields: bracket / engine / card_count exactly as
 * saveDeck derives them, commander / partner from the manifest
 * (deckBoxCommanders). `mismatch` is for the caller's log, never sent.
 */
export function deckBoxCommitFields(
  analysis: CompleteAnalysis,
  manifest: { decklist: string; zone: readonly string[] },
): { fields: DeckBoxCommitFields; mismatch: boolean } {
  const base = savedDeckFields(analysis)
  const cmd = deckBoxCommanders(analysis, manifest)
  return {
    fields: {
      bracket: base.bracket,
      engine: base.engine,
      card_count: base.cardCount,
      commander: cmd.commander,
      partner: cmd.partner,
    },
    mismatch: cmd.mismatch,
  }
}
